import { Text } from '@dx-ui/global-ui-prism';
import cx from 'classnames';
import { useTranslation } from 'next-i18next';
import * as React from 'react';
import { get, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { FormError } from './form.error';
import { FormHelpMessage } from './form.help-message';
import { FormLabel } from './form.label';
import type { FormInputBase } from './form.types';

/**
 * This component is to be used in place of all `<textarea>` elements
 */
const FormTextarea: React.FC<
  React.PropsWithChildren<FormInputBase<React.TextareaHTMLAttributes<HTMLTextAreaElement>>>
> = ({
  label,
  name,
  optional,
  required,
  registerOptions,
  labelClassName,
  helpMessage,
  loading,
  className,
  containerClassName,
  maxLength,
  ...rest
}) => {
  'use no memo';
  const id = React.useId();
  const errorId = `input-error-${id}`;
  const helperId = `input-helper-${id}`;
  const [t] = useTranslation('osc-form');
  const { register } = useFormContext();
  const inputName = name;
  const { errors } = useFormState({ name: inputName });
  const inputValue = useWatch({ name: inputName });
  const fieldError = get(errors, inputName);
  const hasError = !!fieldError;

  const length = inputValue?.length || 0;
  const percent = (maxLength && length / maxLength) || 0;
  const remaining = maxLength && maxLength - length;

  return (
    <div className={containerClassName}>
      <FormLabel
        label={label}
        required={required}
        optional={optional}
        hasError={hasError}
        className={cx('self-start', labelClassName)}
      >
        <div className="relative flex flex-col">
          <textarea
            {...rest}
            maxLength={maxLength}
            required={required}
            data-dd-privacy="mask-user-input"
            className={cx('form-textarea w-full', className, {
              'form-error': hasError,
            })}
            aria-invalid={hasError}
            aria-describedby={`${errorId} ${helperId}`}
            {...register(inputName, {
              required: required ? t('requiredError') : false,
              ...registerOptions,
            })}
          />
          {!!maxLength && (
            <Text
              aria-live="polite"
              size="xs"
              className={cx('pointer-events-none select-none self-end font-normal', {
                'text-surface-muted': percent < 0.8,
                'text-surface-warn': percent >= 0.8 && percent < 1,
                'text-surface-error': percent >= 1,
              })}
              data-testid="form-textarea-counter"
            >
              <span aria-hidden>
                {length}/{maxLength}
              </span>
              <span className="sr-only">{t('remaining', { count: remaining })}</span>
            </Text>
          )}
        </div>
      </FormLabel>
      <FormError id={errorId} error={fieldError} className="!-mt-2" />
      <FormHelpMessage id={helperId} message={helpMessage} isLoading={loading} />
    </div>
  );
};

export { FormTextarea };
export default FormTextarea;
